import { useQuery } from '@tanstack/react-query';
import { Spinner, Alert, Badge, Table } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { useApiClient } from '../../api/client.jsx';

/**
 * #263 Enhance-Admin (P1, read-only): Betreiber-Überblick über den Enhance-
 * Server — Server + Health, Pläne der Master-Org, Customer-Orgs, Websites.
 * Daten: orbit-backend /v1/hosting-ops/enhance/overview → Nexus-Plugin.
 */
function useEnhanceOverview() {
  const { fetch } = useApiClient();
  return useQuery({
    queryKey: ['enhance-admin-overview'],
    queryFn: () => fetch('/v1/hosting-ops/enhance/overview'),
    staleTime: 30_000,
  });
}

const val = (o, ...keys) => {
  for (const k of keys) if (o?.[k] != null && o[k] !== '') return String(o[k]);
  return '—';
};

function Section({ title, rows, cols }) {
  return (
    <div className="mb-4">
      <h5>{title} <Badge color="light" className="text-muted">{rows.length}</Badge></h5>
      {rows.length === 0 ? (
        <p className="text-muted small mb-0">Keine Einträge.</p>
      ) : (
        <Table size="sm" responsive hover className="mb-0">
          <thead><tr>{cols.map((c) => <th key={c.h}>{c.h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id ?? i}>{cols.map((c) => <td key={c.h} className="small">{c.f(r)}</td>)}</tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export function EnhanceAdminPage() {
  const query = useEnhanceOverview();
  const d = query.data || {};

  return (
    <div>
      <PageHeader title="Webhosting (Enhance)" />
      <p className="text-muted">Betreiber-Sicht auf den Enhance-Server: Server, Pläne, Kunden-Orgs und Websites (read-only).</p>

      {query.isLoading && <Spinner />}
      {query.error && <Alert color="danger">Enhance-Übersicht konnte nicht geladen werden (Driver konfiguriert? Orbit → Drivers → Enhance).</Alert>}
      {d.enabled === false && <Alert color="info">Nexus ist nicht konfiguriert (NEXUS_API_URL fehlt).</Alert>}
      {(d.errors?.length > 0) && <Alert color="warning">Teilfehler: {d.errors.join(' · ')}</Alert>}

      {query.data && d.enabled !== false && (
        <>
          <Section title="Server" rows={d.servers ?? []} cols={[
            { h: 'Name', f: (r) => val(r, 'friendlyName', 'hostname', 'name') },
            { h: 'IP', f: (r) => val(r, 'ip', 'publicIp') },
            { h: 'Status', f: (r) => <Badge color={String(val(r, 'status')).toLowerCase() === 'active' ? 'success' : 'secondary'}>{val(r, 'status', 'state')}</Badge> },
            { h: 'Rollen', f: (r) => (Array.isArray(r.roles) ? r.roles.map((x) => x?.role || x).join(', ') : val(r, 'role')) },
          ]} />
          <Section title="Pläne (Master-Org)" rows={d.plans ?? []} cols={[
            { h: 'ID', f: (r) => val(r, 'id') },
            { h: 'Name', f: (r) => val(r, 'name', 'friendlyName') },
            { h: 'Abos', f: (r) => val(r, 'subscriptionsCount', 'subscriptions') },
          ]} />
          <Section title="Kunden-Orgs" rows={d.customers ?? []} cols={[
            { h: 'Name', f: (r) => val(r, 'name') },
            { h: 'Org-ID', f: (r) => <code>{val(r, 'id')}</code> },
            { h: 'Status', f: (r) => val(r, 'status', 'suspended') },
            { h: 'Websites', f: (r) => val(r, 'websitesCount', 'websites') },
          ]} />
          <Section title="Websites" rows={d.websites ?? []} cols={[
            { h: 'Domain', f: (r) => val(r, 'domain', 'primaryDomain') || val(r?.domain, 'domain') },
            { h: 'Website-ID', f: (r) => <code>{val(r, 'id')}</code> },
            { h: 'Status', f: (r) => val(r, 'status', 'kind') },
            { h: 'PHP', f: (r) => val(r, 'phpVersion') },
          ]} />
        </>
      )}
    </div>
  );
}
